import type { QuoteItem } from '@techgitter/contracts';
import { optionQty } from '../../utils/quotePricing';

/**
 * New Desktop (Custom) build-sheet categories.
 *
 * Each category is one slot on the build sheet. Categories flagged
 * `allowMultiple` can hold several parts (e.g. 1TB SSD + 20TB HDD, or two
 * RAM kits) — every part is INCLUDED in the build (they are additional
 * components, NOT alternatives the customer chooses between). The whole
 * build always collapses to a single consolidated summary line.
 *
 * The order here drives both the on-screen card order and the summary line
 * ordering. Mirrors the backend catalog in
 * `sm4rt-api/core/services/quote_service.py`
 * (`_DESKTOP_BUILD_CATEGORY_LABELS` / `_DESKTOP_BUILD_CATEGORY_ORDER`) — keep
 * the two in sync.
 */
export interface DesktopBuildCategory {
  slug: string;
  label: string;
  allowMultiple: boolean;
  /**
   * Default product code (catalog SKU) seeded onto a hand-added ("Blank")
   * part in this category, so the tech doesn't retype the common code.
   * Picker-sourced parts keep the code that comes with them. Editable per
   * part via the product-code autocomplete. Empty string = no default.
   */
  defaultProductCode: string;
}

export const DESKTOP_BUILD_CATEGORIES: DesktopBuildCategory[] = [
  { slug: 'cpu', label: 'CPU', allowMultiple: false, defaultProductCode: 'HW.CPU' },
  { slug: 'cpu_cooler', label: 'CPU Cooler', allowMultiple: false, defaultProductCode: 'HW.Fan.CPUCooler' },
  { slug: 'case', label: 'Case', allowMultiple: false, defaultProductCode: 'HW.Case' },
  { slug: 'motherboard', label: 'Motherboard', allowMultiple: false, defaultProductCode: 'HW.Motherboard' },
  { slug: 'gpu', label: 'GPU', allowMultiple: true, defaultProductCode: 'HW.GPU' },
  { slug: 'psu', label: 'Power Supply', allowMultiple: false, defaultProductCode: 'HW.PSU' },
  { slug: 'ram', label: 'RAM', allowMultiple: true, defaultProductCode: 'HW.RAM.Desktop' },
  { slug: 'case_fans', label: 'Case Fans', allowMultiple: true, defaultProductCode: 'HW.Fan.Case' },
  { slug: 'os', label: 'Operating System', allowMultiple: false, defaultProductCode: 'SW.OS.Windows' },
  { slug: 'storage', label: 'Hard Drive', allowMultiple: true, defaultProductCode: 'HW.SSD.NVME' },
];

export const DESKTOP_BUILD_CATEGORY_ORDER = DESKTOP_BUILD_CATEGORIES.map(
  (c) => c.slug,
);

const CATEGORY_BY_SLUG = new Map(
  DESKTOP_BUILD_CATEGORIES.map((c) => [c.slug, c]),
);

/** Prefix used for tech-added (off-catalog) categories. Each gets a unique
 *  `other_<id>` slug so the backend treats it as its own summary group. */
export const CUSTOM_CATEGORY_PREFIX = 'other';

export const isCustomCategorySlug = (slug: string): boolean =>
  slug === 'other' || slug.startsWith(`${CUSTOM_CATEGORY_PREFIX}_`);

/** Default display label for a slug — catalog label, else "Other". */
export const desktopBuildCategoryLabel = (slug: string): string =>
  CATEGORY_BY_SLUG.get(slug)?.label ?? 'Other';

/** Whether a slug allows multiple parts. Tech-added categories default to
 *  allowing multiple (a build might add two of the same off-catalog part). */
export const desktopBuildCategoryAllowsMultiple = (slug: string): boolean =>
  CATEGORY_BY_SLUG.get(slug)?.allowMultiple ?? true;

/** Default product code for a slug — empty for tech-added / unknown slugs. */
export const desktopBuildDefaultProductCode = (slug: string): string =>
  CATEGORY_BY_SLUG.get(slug)?.defaultProductCode ?? '';

/**
 * Compose the category-labelled build summary. Mirrors the backend's
 * `_build_desktop_build_description` so the "Regenerate from build" button
 * produces identical text to a fresh-create seed. Groups each part's
 * selected option under its category, in build-sheet order (tech-added
 * categories last, first-seen), quantity-aware, joined with " + ". The
 * per-part internal note is never referenced.
 */
export const buildDesktopBuildDescription = (items: QuoteItem[]): string => {
  const groups = new Map<string, { label: string; parts: string[] }>();
  const seenOrder: string[] = [];
  const sorted = [...(items || [])].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0),
  );
  for (const item of sorted) {
    if (!item.markedForInvoice) continue;
    const opt = item.options.find((o) => o.id === item.selectedOptionId);
    let desc = (opt?.description || '').trim();
    if (!opt || !desc) continue;
    const qty = optionQty(opt);
    if (qty > 1) desc = `${qty}x ${desc}`;
    const slug = item.category || 'other';
    const label =
      (item.name || '').trim() || desktopBuildCategoryLabel(slug);
    let grp = groups.get(slug);
    if (!grp) {
      grp = { label, parts: [] };
      groups.set(slug, grp);
      seenOrder.push(slug);
    }
    grp.parts.push(desc);
  }
  const ordered = [
    ...DESKTOP_BUILD_CATEGORY_ORDER.filter((s) => groups.has(s)),
    ...seenOrder.filter((s) => !DESKTOP_BUILD_CATEGORY_ORDER.includes(s)),
  ];
  return ordered
    .map((s) => {
      const grp = groups.get(s)!;
      return `${grp.label}: ${grp.parts.join(' + ')}`;
    })
    .join('\n');
};
